import { AxiosError } from 'axios';

export interface ApiErrorResponse {
    success: false;
    message: string;
    errors?: Array<{
        field: string;
        message: string;
        value?: any;
    }>;
    code?: string;
    timestamp: string;
}

export interface ValidationError {
    field: string;
    message: string;
    value?: any;
}

export class ErrorHandler {
    /**
     * Extract user-friendly error message from API response
     */
    static getErrorMessage(error: AxiosError): string {
        const response = error.response?.data as ApiErrorResponse;
        const status = error.response?.status;
        const url = error.config?.url || '';
        const rawData = (error.config as any)?.data;
        let payload: any = rawData;
        if (typeof rawData === 'string') {
            try { payload = JSON.parse(rawData); } catch { /* ignore */ }
        }

        // Friendly, non-technical copy for fermentation maintenance common cases
        if ((status === 400 || status === 422) && url.includes('fermentation-maintenance')) {
            const fermentationId = payload?.fermentation_id;
            const wineBatchId = payload?.wine_batch_id;
            const serverMsg = (response?.message || '').toLowerCase();

            // No batch chosen
            if ((fermentationId === undefined || fermentationId === null || fermentationId === '') &&
                (wineBatchId === undefined || wineBatchId === null || wineBatchId === '')) {
                return 'Please select a wine batch before saving the maintenance record.';
            }

            if (serverMsg.includes('no fermentation found for provided batch')) {
                return 'The selected batch has no fermentation yet. Please choose a different batch.';
            }

            if (serverMsg.includes('invalid wine_batch_id')) {
                return 'The selected wine batch is invalid. Please pick a batch from the list.';
            }
        }

        if (response?.message) {
            return response.message;
        }

        // Handle legacy error formats
        if (response?.errors && Array.isArray(response.errors)) {
            return response.errors.map(err => err.message || (err as any).msg || 'Validation error').join(', ');
        }

        // Handle express-validator format
        if (response?.errors && Array.isArray(response.errors)) {
            return response.errors.map((err: any) => err.msg || 'Validation error').join(', ');
        }

        // Handle generic message field
        if (response?.message) {
            return response.message;
        }

        // Fallback to HTTP status message
        switch (error.response?.status) {
            case 400:
                return 'Invalid request. Please check your input.';
            case 401:
                return 'You are not authorized to perform this action.';
            case 403:
                return 'Access denied.';
            case 404:
                return 'The requested resource was not found.';
            case 409:
                return 'This action conflicts with existing data.';
            case 422:
                return 'Validation failed. Please check your input.';
            case 500:
                return 'Server error occurred. Please try again later.';
            default:
                return error.message || 'An unexpected error occurred.';
        }
    }

    /**
     * Extract validation errors from API response
     */
    static getValidationErrors(error: AxiosError): ValidationError[] {
        const response = error.response?.data as ApiErrorResponse;

        if (response?.errors && Array.isArray(response.errors)) {
            return response.errors.map(err => ({
                field: err.field || 'unknown',
                message: err.message || 'Validation error',
                value: err.value
            }));
        }

        // Infer validation from context and common messages when no structured errors exist
        const status = error.response?.status;
        const message = (response?.message || error.message || '').toLowerCase();
        const url = error.config?.url || '';
        const rawData = (error.config as any)?.data;
        let payload: any = rawData;
        if (typeof rawData === 'string') {
            try { payload = JSON.parse(rawData); } catch { /* ignore */ }
        }

        const inferred: ValidationError[] = [];

        if (status === 400 || status === 422) {
            // From server message
            if (message.includes('fermentation_id or wine_batch_id is required')) {
                inferred.push({ field: 'wine_batch_id', message: 'Please select a wine batch.' });
            }
            if (message.includes('no fermentation found for provided batch')) {
                inferred.push({ field: 'wine_batch_id', message: 'Selected batch has no fermentation.' });
            }
            if (message.includes('invalid wine_batch_id')) {
                inferred.push({ field: 'wine_batch_id', message: 'Selected wine batch is invalid.' });
            }

            // From request context (friendlier UX for missing batch selection)
            if (url.includes('fermentation-maintenance')) {
                const fermentationId = payload?.fermentation_id;
                const wineBatchId = payload?.wine_batch_id;
                if ((fermentationId === undefined || fermentationId === null || fermentationId === '') &&
                    (wineBatchId === undefined || wineBatchId === null || wineBatchId === '')) {
                    inferred.push({ field: 'wine_batch_id', message: 'Please select a wine batch.' });
                }
            }
        }

        return inferred;
    }

    /**
     * Check if error is a validation error
     */
    static isValidationError(error: AxiosError): boolean {
        const status = error.response?.status;
        return status === 400 || status === 422;
    }

    /**
     * Check if error is an authentication error
     */
    static isAuthError(error: AxiosError): boolean {
        const status = error.response?.status;
        return status === 401 || status === 403;
    }

    /**
     * Check if error is a network error
     */
    static isNetworkError(error: AxiosError): boolean {
        return !error.response && error.request;
    }

    /**
     * Get error code from API response
     */
    static getErrorCode(error: AxiosError): string | undefined {
        const response = error.response?.data as ApiErrorResponse;
        return response?.code;
    }

    /**
     * Format error for display in UI
     */
    static formatError(error: AxiosError): {
        message: string;
        code?: string;
        isValidation: boolean;
        isAuth: boolean;
        isNetwork: boolean;
        validationErrors: ValidationError[];
    } {
        return {
            message: this.getErrorMessage(error),
            code: this.getErrorCode(error),
            isValidation: this.isValidationError(error),
            isAuth: this.isAuthError(error),
            isNetwork: this.isNetworkError(error),
            validationErrors: this.getValidationErrors(error)
        };
    }

    /**
     * Log error for debugging
     */
    static logError(error: AxiosError, context?: string): void {
        if (process.env.NODE_ENV !== 'development') {
            return;
        }

        const formatted = this.formatError(error);

        console.group(`Error${context ? ` in ${context}` : ''}`);
        console.error('Message:', formatted.message);
        console.error('Code:', formatted.code);
        console.error('Status:', error.response?.status);
        console.error('URL:', error.config?.url);
        console.error('Method:', error.config?.method?.toUpperCase());

        if (formatted.validationErrors.length > 0) {
            console.error('Validation Errors:', formatted.validationErrors);
        }

        console.groupEnd();
    }
}

/**
 * Common error messages for consistent UI
 */
export const ErrorMessages = {
    NETWORK_ERROR: 'Network error. Please check your connection.',
    SERVER_ERROR: 'Server error occurred. Please try again later.',
    VALIDATION_ERROR: 'Please check your input and try again.',
    AUTH_ERROR: 'Authentication required. Please log in.',
    PERMISSION_ERROR: 'You do not have permission to perform this action.',
    NOT_FOUND: 'The requested resource was not found.',
    CONFLICT: 'This action conflicts with existing data.',
    TIMEOUT: 'Request timed out. Please try again.',
    UNKNOWN: 'An unexpected error occurred.'
} as const;

/**
 * Hook for consistent error handling in React components
 */
export const useErrorHandler = () => {
    const handleError = (error: AxiosError, context?: string) => {
        ErrorHandler.logError(error, context);
        return ErrorHandler.formatError(error);
    };

    return { handleError };
};
